import {lazy} from '@aboutyou/lazy-imports';
import {useIsStorageConsentGiven} from '@aboutyou/storage';
import {
  TADARIDA_OPTIONAL_USER_PERSIST_KEY,
  useOptionalUser,
} from '@aboutyou/user';
import {doAsync, localStorageRemoveItem} from '@aboutyou/utils';
import React, {useEffect} from 'react';
import {AUTH_FLOW_STATE} from '../../features/Authentication/hooks/state';
import {useIsAuthenticated} from '../../features/Authentication/hooks/useIsAuthenticated';

const LoadCustomerDataLazy = lazy(() =>
  import('./LoadCustomerData.eager').then(mod => ({
    default: mod.LoadCustomerData,
  })),
);

export const LoadCustomerDataConditional = () => {
  const {isUserDataLoading, isAuthenticated} = useIsAuthenticated();
  const isStorageConsentGiven = useIsStorageConsentGiven();
  const {isLoading: optionalUserLoading, data: optionalUser} =
    useOptionalUser();
  const optionalUserData = optionalUser?.userData;

  /**
   * This effect is responsible for removing any stale optional user data from the local storage if the user is logged out
   * This can happen in the following case:
   * - User login in one language (DE-DE) and -> switches to another language (EN-DE) -> logs out -> then switches back to the original language (DE-DE)
   * - in this case we need to remove the optional user data from the local storage
   *
   * Reload the page to restart the session, like we when user logs out
   */
  useEffect(() => {
    if (isUserDataLoading || optionalUserLoading) {
      return;
    }
    if (optionalUserData && !isAuthenticated) {
      localStorageRemoveItem(TADARIDA_OPTIONAL_USER_PERSIST_KEY);
      doAsync(() => {
        window.location.reload();
      });
    }
  }, [
    isAuthenticated,
    isUserDataLoading,
    optionalUserData,
    optionalUserLoading,
  ]);

  /**
   * If the user is manually loggingIn in this shop session, we dont want to load the customer data from the backend since we already get the optional user data from login endpoints,
   * currently we have no reactish way to know if the user is logged in in this session, or are we reading the login data from cookies.
   * Thus this workaround where AUTH_FLOW_STATE is set to true in the useAuthResponseHandler hook when the user logs in.
   * This prevents additional backend calls to load the customer data when the user login in this session.
   */
  if (AUTH_FLOW_STATE.completedInThisSession) {
    return null;
  }

  if (!isStorageConsentGiven) {
    return null;
  }

  if (isUserDataLoading || optionalUserLoading || optionalUserData) {
    return null;
  }

  return <LoadCustomerDataLazy />;
};
